"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ArrowRight, FolderOpen, Plus, Search, Share } from "lucide-react";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import type { CNSProject } from "@/models/cns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EntityActionsMenu } from "@/components/entity-actions-menu";
import { ShareDialog } from "@/components/share-dialog";
import { WorkspaceAnalyticsGlance } from "@/components/analytics/workspace-analytics-glance";
import { formatRelative } from "@/components/project-home/ProjectHomeRowPrimitives";
import { buildProjectHomePath } from "@/lib/entityDeepLinks";
import { cn } from "@/lib/utils";
const SEARCH_THRESHOLD = 3;
function projectActivityMs(project: CNSProject): number {
  const source = project.updatedAt ?? project.createdAt;
  return new Date(source).getTime();
}
function sortByRecentActivity(projects: CNSProject[]): CNSProject[] {
  return [...projects].sort((a, b) => projectActivityMs(b) - projectActivityMs(a));
}
function filterProjectsByQuery(projects: CNSProject[], query: string): CNSProject[] {
  const q = query.trim().toLowerCase();
  if (!q) return projects;
  return projects.filter(p => p.title.toLowerCase().includes(q));
}
interface WorkspaceProjectRowProps {
  project: CNSProject;
  shared?: boolean;
  onPrefetch: (projectId: string) => void;
  onShare?: (project: {
    id: string;
    title: string;
  }) => void;
  onDelete?: (projectId: string) => Promise<void>;
}
function WorkspaceProjectRow(t0) {
  const $ = _c(31);
  const {
    project,
    shared: t1,
    onPrefetch,
    onShare,
    onDelete
  } = t0;
  const shared = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== project.id) {
    t2 = buildProjectHomePath(project.id);
    $[0] = project.id;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const href = t2;
  const showActions = !shared && onShare && onDelete;
  const dateSource = project.updatedAt ?? project.createdAt;
  let t3;
  if ($[2] !== onPrefetch || $[3] !== project.id) {
    t3 = () => onPrefetch(project.id);
    $[2] = onPrefetch;
    $[3] = project.id;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cn("flex min-w-0 flex-1 items-center gap-3 rounded-lg px-2 py-3", "transition-colors hover:bg-muted/50", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50");
    t5 = <FolderOpen className="size-4 shrink-0 text-muted-foreground/70 group-hover:text-foreground" aria-hidden={true} />;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  let t6;
  if ($[7] !== project.title) {
    t6 = <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{project.title}</span>;
    $[7] = project.title;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = shared ? "Shared \xB7 " : "";
  let t8;
  if ($[9] !== dateSource) {
    t8 = formatRelative(dateSource);
    $[9] = dateSource;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t7 || $[12] !== t8) {
    t9 = <span className="shrink-0 text-xs text-muted-foreground tabular-nums">{t7}{t8}</span>;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <ArrowRight className="size-4 shrink-0 text-muted-foreground/0 transition-all group-hover:text-muted-foreground/60" aria-hidden={true} />;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== href || $[16] !== t6 || $[17] !== t9) {
    t11 = <Link href={href} className={t4}>{t5}{t6}{t9}{t10}</Link>;
    $[15] = href;
    $[16] = t6;
    $[17] = t9;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== onDelete || $[20] !== onShare || $[21] !== project.id || $[22] !== project.title || $[23] !== showActions) {
    t12 = showActions ? <div className="shrink-0 pr-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100" onClick={_temp}><EntityActionsMenu ariaLabel={`Actions for ${project.title}`} entityId={project.id} entityTitle={project.title} entityType="Project" onDelete={onDelete}><DropdownMenuItem inset={true} onClick={() => onShare({
          id: project.id,
          title: project.title
        })}><span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center"><Share className="size-4" /></span>Share</DropdownMenuItem></EntityActionsMenu></div> : null;
    $[19] = onDelete;
    $[20] = onShare;
    $[21] = project.id;
    $[22] = project.title;
    $[23] = showActions;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t11 || $[26] !== t12) {
    t13 = <div className="flex items-center gap-1">{t11}{t12}</div>;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t13 || $[29] !== t3) {
    t14 = <li className="group relative" onPointerEnter={t3}>{t13}</li>;
    $[28] = t13;
    $[29] = t3;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  return t14;
}
function _temp(e) {
  return e.stopPropagation();
}
function ProjectList(t0) {
  const $ = _c(13);
  const {
    projects,
    shared,
    onPrefetch,
    onShare,
    onDelete
  } = t0;
  let t1;
  if ($[0] !== onDelete || $[1] !== onPrefetch || $[2] !== onShare || $[3] !== projects || $[4] !== shared) {
    let t2;
    if ($[6] !== onDelete || $[7] !== onPrefetch || $[8] !== onShare || $[9] !== shared) {
      t2 = project => <WorkspaceProjectRow key={project.id} project={project} shared={shared} onPrefetch={onPrefetch} onShare={onShare} onDelete={onDelete} />;
      $[6] = onDelete;
      $[7] = onPrefetch;
      $[8] = onShare;
      $[9] = shared;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    t1 = projects.map(t2);
    $[0] = onDelete;
    $[1] = onPrefetch;
    $[2] = onShare;
    $[3] = projects;
    $[4] = shared;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[11] !== t1) {
    t2 = <ul role="list" className="divide-y divide-border/40">{t1}</ul>;
    $[11] = t1;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  return t2;
}
function ListSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <ul role="list" className="divide-y divide-border/40" aria-hidden={true}>{[0, 1, 2].map(_temp2)}</ul>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp2(i) {
  return <li key={i} className="flex items-center gap-3 px-2 py-3"><div className="size-4 rounded bg-muted/60" /><div className="h-4 flex-1 rounded bg-muted" /><div className="h-3 w-16 rounded bg-muted/50" /></li>;
}
export interface WorkspaceProjectsGridProps {
  ownedProjects: CNSProject[];
  sharedProjects: CNSProject[];
  loading: boolean;
  onCreateProject: () => void;
  onDeleteProject: (projectId: string) => Promise<void>;
}

/**
 * Bet C workspace launcher — flat recent-projects list (Cursor welcome style), not a card dashboard.
 */
export function WorkspaceProjectsGrid(t0) {
  const $ = _c(52);
  const {
    ownedProjects,
    sharedProjects,
    loading,
    onCreateProject,
    onDeleteProject
  } = t0;
  const router = useRouter();
  const [query, setQuery] = React.useState("");
  const [shareDialogOpen, setShareDialogOpen] = React.useState(false);
  const [shareTarget, setShareTarget] = React.useState(null);
  let t1;
  if ($[0] !== ownedProjects) {
    t1 = sortByRecentActivity(ownedProjects);
    $[0] = ownedProjects;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const sortedOwned = t1;
  let t2;
  if ($[2] !== sharedProjects) {
    t2 = sortByRecentActivity(sharedProjects);
    $[2] = sharedProjects;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const sortedShared = t2;
  const totalCount = sortedOwned.length + sortedShared.length;
  const showSearch = !loading && totalCount > SEARCH_THRESHOLD;
  let t3;
  if ($[4] !== query || $[5] !== sortedOwned) {
    t3 = filterProjectsByQuery(sortedOwned, query);
    $[4] = query;
    $[5] = sortedOwned;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const filteredOwned = t3;
  let t4;
  if ($[7] !== query || $[8] !== sortedShared) {
    t4 = filterProjectsByQuery(sortedShared, query);
    $[7] = query;
    $[8] = sortedShared;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const filteredShared = t4;
  let t5;
  if ($[10] !== router) {
    t5 = projectId => {
      router.prefetch(buildProjectHomePath(projectId));
    };
    $[10] = router;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const prefetchProject = t5;
  let t6;
  let t7;
  if ($[12] !== prefetchProject || $[13] !== sortedOwned || $[14] !== sortedShared) {
    t6 = () => {
      const all = [...sortedOwned, ...sortedShared];
      if (all.length === 0) {
        return;
      }
      const runtime = globalThis as typeof globalThis & {
        requestIdleCallback?: (callback: (deadline: {
          didTimeout: boolean;
          timeRemaining: () => number;
        }) => void, options?: {
          timeout?: number;
        }) => number;
        cancelIdleCallback?: (handle: number) => void;
      };
      const runPrefetch = () => {
        for (const project of all) {
          prefetchProject(project.id);
        }
      };
      if (typeof runtime.requestIdleCallback === "function") {
        const idleId = runtime.requestIdleCallback(runPrefetch, {
          timeout: 1500
        });
        return () => runtime.cancelIdleCallback?.(idleId);
      }
      const timeout = globalThis.setTimeout(runPrefetch, 300);
      return () => globalThis.clearTimeout(timeout);
    };
    t7 = [sortedOwned, sortedShared, prefetchProject];
    $[12] = prefetchProject;
    $[13] = sortedOwned;
    $[14] = sortedShared;
    $[15] = t6;
    $[16] = t7;
  } else {
    t6 = $[15];
    t7 = $[16];
  }
  React.useEffect(t6, t7);
  if (loading) {
    let t8;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <WorkspaceAnalyticsGlance />;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    let t9;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <div className="mx-auto w-full max-w-lg">{t8}<div className="mt-8"><ListSkeleton /></div></div>;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    return t9;
  }
  if (totalCount === 0) {
    let t8;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <WorkspaceAnalyticsGlance />;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    let t9;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <p className="text-sm text-muted-foreground">No projects yet.</p>;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    let t10;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <Plus className="h-4 w-4" />;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    let t11;
    if ($[22] !== onCreateProject) {
      t11 = <div className="mx-auto w-full max-w-lg">{t8}<div className="mt-8 flex flex-col items-center gap-6 py-8 text-center">{t9}<Button className="gap-2" onClick={onCreateProject}>{t10}New project</Button></div></div>;
      $[22] = onCreateProject;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    return t11;
  }
  let t8;
  if ($[24] !== filteredOwned || $[25] !== filteredShared || $[26] !== query) {
    t8 = query.trim().length > 0 && filteredOwned.length === 0 && filteredShared.length === 0;
    $[24] = filteredOwned;
    $[25] = filteredShared;
    $[26] = query;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  const noMatches = t8;
  let t9;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <WorkspaceAnalyticsGlance />;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground/55">Your projects</p>;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== query || $[31] !== showSearch) {
    t11 = showSearch ? <div className="relative"><Search className="pointer-events-none absolute left-0 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden={true} /><Input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder={"Search projects\u2026"} className="border-0 border-b border-border/60 bg-transparent px-0 pl-7 shadow-none rounded-none focus-visible:ring-0 focus-visible:border-foreground/30" aria-label="Search projects" /></div> : null;
    $[30] = query;
    $[31] = showSearch;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== filteredOwned || $[34] !== filteredShared || $[35] !== noMatches || $[36] !== onDeleteProject || $[37] !== prefetchProject || $[38] !== query) {
    t12 = noMatches ? <p className="py-6 text-center text-sm text-muted-foreground">No projects match “{query.trim()}”.</p> : <>{filteredOwned.length > 0 ? <ProjectList projects={filteredOwned} onPrefetch={prefetchProject} onShare={target => {
        setShareTarget(target);
        setShareDialogOpen(true);
      }} onDelete={onDeleteProject} /> : null}{filteredShared.length > 0 ? <div className={filteredOwned.length > 0 ? "space-y-2 pt-2" : "space-y-2"}><p className="px-2 text-xs font-medium uppercase tracking-wide text-muted-foreground/80">Shared with you</p><ProjectList projects={filteredShared} shared={true} onPrefetch={prefetchProject} /></div> : null}</>;
    $[33] = filteredOwned;
    $[34] = filteredShared;
    $[35] = noMatches;
    $[36] = onDeleteProject;
    $[37] = prefetchProject;
    $[38] = query;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  let t14;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = cn("flex w-full items-center gap-3 rounded-lg border border-dashed border-border/60 px-2 py-3", "text-sm text-muted-foreground transition-colors", "hover:border-border hover:bg-muted/30 hover:text-foreground", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50");
    t14 = <Plus className="size-4 shrink-0" aria-hidden={true} />;
    $[40] = t13;
    $[41] = t14;
  } else {
    t13 = $[40];
    t14 = $[41];
  }
  let t15;
  if ($[42] !== onCreateProject) {
    t15 = <button type="button" onClick={onCreateProject} className={t13}>{t14}New project</button>;
    $[42] = onCreateProject;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== shareDialogOpen || $[45] !== shareTarget) {
    t16 = shareTarget ? <ShareDialog open={shareDialogOpen} onOpenChange={open => {
      setShareDialogOpen(open);
      if (!open) {
        setShareTarget(null);
      }
    }} entityType="project" entityPublicId={shareTarget.id} entityLabel={shareTarget.title} /> : null;
    $[44] = shareDialogOpen;
    $[45] = shareTarget;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== t11 || $[48] !== t12 || $[49] !== t15 || $[50] !== t16) {
    t17 = <div className="mx-auto w-full max-w-lg">{t9}<div className="mt-8 space-y-3">{t10}<div className="space-y-4">{t11}{t12}{t15}{t16}</div></div></div>;
    $[47] = t11;
    $[48] = t12;
    $[49] = t15;
    $[50] = t16;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  return t17;
}